import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { adminService, User } from '../services/adminService'
import { UserList } from '../components/UserList'
import { useTranslation } from '../i18n/hooks/useTranslation'
import { Button } from '../components/ui/Button'

export function UsersPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended' | 'deleted'>('all')
  const [roleFilter, setRoleFilter] = useState<'all' | 'user' | 'moderator' | 'admin'>('all')

  const { data: allUsers = [], isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => adminService.getUsers(),
  })

  // Filter users based on status and role
  const filteredUsers = allUsers.filter(user => {
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter
    const matchesRole = roleFilter === 'all' || user.role === roleFilter
    return matchesStatus && matchesRole
  })

  // Calculate user stats
  const userStats = {
    total: allUsers.length,
    active: allUsers.filter(u => u.status === 'active').length,
    suspended: allUsers.filter(u => u.status === 'suspended').length,
    deleted: allUsers.filter(u => u.status === 'deleted').length,
  }

  const handleEdit = (user: User) => {
    navigate({ to: `/users/${user.id}` })
  }

  const clearFilters = () => {
    setStatusFilter('all')
    setRoleFilter('all')
  }

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('common.loading')}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      {/* Enhanced Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
              {t('users.title')}
            </h1>
            <p className="text-gray-600 mt-2 text-sm lg:text-base leading-relaxed">
              {t('users.subtitle')}
            </p>
            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm text-red-600 font-medium">
                    {t('errors.loadingUsers')}: {error.message}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 text-gray-700 rounded-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="text-sm font-medium">{allUsers.length} {t('users.userStats.total')}</span>
            </div>
          </div>
        </div>
      </div>


      {/* User Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-900">{userStats.total}</div>
          <div className="text-sm text-gray-500">{t('users.userStats.total')}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-900">{userStats.active}</div>
          <div className="text-sm text-gray-500">{t('users.userStats.active')}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-900">{userStats.suspended}</div>
          <div className="text-sm text-gray-500">{t('users.userStats.suspended')}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-900">{userStats.deleted}</div>
          <div className="text-sm text-gray-500">{t('users.userStats.deleted')}</div>
        </div>
      </div>

      {/* Enhanced Filter Controls */}
      {allUsers.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('filters.filterByStatus')}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
              >
                <option value="all">{t('users.allUsers')}</option>
                <option value="active">{t('common.active')}</option>
                <option value="suspended">{t('common.suspended')}</option>
                <option value="deleted">{t('common.deleted')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('filters.filterByRole')}
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as any)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
              >
                <option value="all">{t('filters.allRoles')}</option>
                <option value="user">{t('users.roles.user')}</option>
                <option value="moderator">{t('users.roles.moderator')}</option>
                <option value="admin">{t('users.roles.admin')}</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={clearFilters}
                disabled={statusFilter === 'all' && roleFilter === 'all'}
                variant="secondary"
                className="w-full"
              >
                {t('filters.clearFilters')}
              </Button>
            </div>
          </div>
          {(statusFilter !== 'all' || roleFilter !== 'all') && (
            <div className="mt-3 text-sm text-gray-600">
              {t('filters.showing')} {filteredUsers.length} {t('filters.of')} {allUsers.length} {t('filters.results')}
            </div>
          )}
        </div>
      )}

      {filteredUsers.length === 0 ? (
        <div className="text-center py-12">
          <div className="max-w-md mx-auto">
            <div className="text-6xl mb-4">👥</div>
            {allUsers.length === 0 ? (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t('users.noUsersFound')}
                </h3>
                <p className="text-gray-500 mb-6">
                  {t('users.usersWillAppear')}
                </p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t('users.noUsersFoundSearch')}
                </h3>
                <p className="text-gray-500 mb-6">
                  {t('users.tryDifferentSearch')}
                </p>
                <Button
                  onClick={clearFilters}
                  variant="primary"
                >
                  {t('filters.clearFilters')}
                </Button>
              </>
            )}
          </div>
        </div>
      ) : (
        <UserList
          users={filteredUsers}
          onEdit={handleEdit}
        />
      )}
    </div>
  )
}